import { authApi } from '@milliswift/api-client'
import { Button, Spinner } from '@milliswift/ui'
import { CheckCircle2, MailWarning } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AuthCard, AuthPage } from '../components/AuthCard'

/**
 * Email verification. The token arrives in the URL, so this page's whole job
 * is one call and three states: pending, done, failed.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [state, setState] = useState<'pending' | 'done' | 'error'>(token ? 'pending' : 'error')

  useEffect(() => {
    if (!token) return
    let cancelled = false

    authApi
      .verifyEmail(token)
      .then(() => !cancelled && setState('done'))
      .catch(() => !cancelled && setState('error'))

    return () => {
      cancelled = true
    }
  }, [token])

  if (state === 'pending') {
    return (
      <AuthPage>
        <AuthCard title="Verifying your email">
          <div className="flex flex-col items-center gap-4 py-4">
            <Spinner className="h-6 w-6 text-subtle" />
            <p className="text-sm text-muted">One moment…</p>
          </div>
        </AuthCard>
      </AuthPage>
    )
  }

  if (state === 'done') {
    return (
      <AuthPage>
        <AuthCard title="Email verified">
          <div className="flex flex-col items-center gap-4 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-success-soft">
              <CheckCircle2 className="h-6 w-6 text-success-text" aria-hidden />
            </span>
            <p className="text-sm text-muted">Your account is active. Redirecting you to sign in…</p>
            <Button className="w-full" onClick={() => (window.location.href = '/login?verified=true')}>
              Continue to sign in
            </Button>
          </div>
        </AuthCard>
      </AuthPage>
    )
  }

  return (
    <AuthPage>
      <AuthCard title="This link has expired">
        <div className="flex flex-col items-center gap-4 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-warning-soft">
            <MailWarning className="h-6 w-6 text-warning-text" aria-hidden />
          </span>
          <p className="text-sm text-muted">
            Verification links are single-use and expire after a while. Request a fresh one
            and try again.
          </p>
          <Button variant="outline" className="w-full" onClick={() => (window.location.href = '/login')}>
            Back to sign in
          </Button>
        </div>
      </AuthCard>
    </AuthPage>
  )
}
